import type { AxiosInstance, AxiosRequestConfig, Method } from 'axios';
import axios, { AxiosError } from 'axios';

import type {
  HttpClientResponse,
  IHttpClient,
  IHttpRequestConfig,
  JSendValidationContractErrorResponse,
  JSendValidationErrorResponse,
} from '@/services/api/HttpClient';
import type { UseLogger } from '@/services/logger/useLogger';

export abstract class HttpAxiosBaseClient implements IHttpClient {
  protected axios: AxiosInstance;

  protected logger: UseLogger;

  protected bedarfId: string | undefined;

  protected token: string | undefined;

  protected constructor(
    config: AxiosRequestConfig,
    logger: UseLogger,
    bedarfId: string | undefined,
    token: string | undefined,
  ) {
    this.axios = axios.create(config);
    this.logger = logger;
    this.bedarfId = bedarfId;
    this.token = token;

    this.axios.interceptors.response.use(
      (value) => value,
      (error: AxiosError) => this.onError(error),
    );
  }

  public setBedarfId(bedarfId: string | undefined) {
    this.bedarfId = bedarfId;
  }

  public setToken(token: string | undefined) {
    this.token = token;
  }

  public getToken(): string | undefined {
    return this.token;
  }

  protected buildAxiosConfig(config: IHttpRequestConfig): AxiosRequestConfig {
    const params = { ...config.params };
    if (this.bedarfId !== undefined) {
      params.bedarfId = this.bedarfId;
    }

    if (this.token !== undefined) {
      params.token = this.token;
    }

    return {
      params,
      data: config.data,
    };
  }

  protected abstract doRequest<T>(
    httpMethod: Method,
    url: string,
    config?: IHttpRequestConfig,
  ): HttpClientResponse<T>;

  protected abstract onError(error: AxiosError): void;

  protected abstract handleValidationErrorResponse(
    res: JSendValidationErrorResponse | JSendValidationContractErrorResponse,
  ): void;

  get<T>(url: string, config?: IHttpRequestConfig): HttpClientResponse<T> {
    return this.doRequest('GET', url, config);
  }

  post<T>(url: string, config?: IHttpRequestConfig): HttpClientResponse<T> {
    return this.doRequest('POST', url, config);
  }

  put<T>(url: string, config?: IHttpRequestConfig): HttpClientResponse<T> {
    return this.doRequest('PUT', url, config);
  }

  patch<T>(url: string, config?: IHttpRequestConfig): HttpClientResponse<T> {
    return this.doRequest('PATCH', url, config);
  }

  delete<T>(url: string, config?: IHttpRequestConfig): HttpClientResponse<T> {
    return this.doRequest('DELETE', url, config);
  }

  getBaseURL(): string {
    return this.axios.defaults.baseURL ?? '';
  }
}
